import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartStore, useAuthStore } from '../store';
import { placeOrder, getShippingSettings } from '../firebase/database';
import { formatPrice, generateOrderId, PAKISTAN_CITIES } from '../utils/helpers';
import SEO from '../components/common/SEO';
import { Spinner } from '../components/common/Loader';
import { trackInitiateCheckout, trackPurchase } from '../utils/pixel';
import toast from 'react-hot-toast';

const CheckoutPage = () => {
  const { items, getTotal, clearCart } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orderDone, setOrderDone] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [shippingSettings, setShippingSettings] = useState(null);
  const [openPayInfo, setOpenPayInfo] = useState(null);

  const [form, setForm] = useState({
    firstName: user?.displayName?.split(' ')[0] || '',
    lastName: user?.displayName?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    notes: '',
    paymentMethod: '',
  });

  useEffect(() => {
    getShippingSettings().then(s => setShippingSettings(s));
  }, []);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  // Always use salePrice if available, otherwise price
  const subtotal = items.reduce((s, i) => s + (i.salePrice && i.salePrice < i.price ? i.salePrice : i.price) * i.quantity, 0);

  useEffect(() => {
    if (items.length > 0) trackInitiateCheckout(items, subtotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getShippingRule = () => {
    if (!shippingSettings?.rules?.length || !form.city) return null;
    const isKarachi = form.city.toLowerCase() === 'karachi';
    const cityKey = isKarachi ? 'Karachi' : 'Other';
    return shippingSettings.rules.find(r =>
      r.city === cityKey &&
      totalQty >= (r.minQty || 1) &&
      totalQty <= (r.maxQty || 999)
    ) || null;
  };

  const shippingRule = getShippingRule();
  // Default 500 if no rule found for city
  const shippingCost = form.city ? (shippingRule?.price ?? 500) : 0;
  // COD only if rule explicitly allows it, default false if no rule
  const codAllowed = shippingRule?.codAllowed || false;
  const total = subtotal + shippingCost;
  const paymentAccounts = shippingSettings?.paymentAccounts || {};

  const getPaymentMethods = () => {
    const methods = [];
    if (codAllowed) methods.push({ id: 'cod', label: 'Cash on Delivery', sub: 'Pay when order arrives at your door' });
    methods.push({ id: 'jazzcash', label: 'JazzCash', sub: 'Pay via JazzCash mobile wallet' });
    methods.push({ id: 'easypaisa', label: 'EasyPaisa', sub: 'Pay via EasyPaisa account' });
    methods.push({ id: 'bank', label: 'Bank Transfer', sub: 'Direct bank transfer' });
    return methods;
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.firstName || !form.email || !form.phone) { toast.error('Fill all required fields'); return false; }
      if (!form.address || !form.city) { toast.error('Fill shipping address'); return false; }
    }
    if (step === 1) {
      if (!form.paymentMethod) { toast.error('Select a payment method'); return false; }
    }
    return true;
  };

  const handlePlace = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const oid = generateOrderId();
      const orderData = {
        orderId: oid,
        userId: user?.uid || 'guest',
        customerName: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email, phone: form.phone,
        shippingAddress: { address: form.address, city: form.city },
        shippingRule: shippingRule?.label || 'Standard',
        shippingCost,
        paymentMethod: form.paymentMethod,
        items: items.map(i => ({
          id: i.id, title: i.title,
          price: i.salePrice && i.salePrice < i.price ? i.salePrice : i.price,
          originalPrice: i.price,
          quantity: i.quantity,
          selectedVariation: i.selectedVariation,
          image: i.images?.[0] || ''
        })),
        subtotal, total, notes: form.notes, totalQty,
      };
      await placeOrder(orderData);
      trackPurchase(oid, orderData.items, total);
      setOrderId(oid);
      clearCart();
      setOrderDone(true);
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (err) {
      toast.error('Failed to place order. Please try again.');
      console.error(err);
    } finally { setLoading(false); }
  };

  if (!items.length && !orderDone) { navigate('/cart'); return null; }

  // ─── ORDER SUCCESS PAGE ───────────────────────────────────────────────────────
  if (orderDone) return (
    <div className="min-h-screen bg-[#f8f9fc]">
      <div className="bg-[#1a3a6e] py-10">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.1 }}
            className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <p className="text-green-300 text-sm font-semibold mb-1">Order Confirmed!</p>
            <h1 className="font-display text-3xl font-black text-white mb-2">Thank you, {form.firstName}!</h1>
            <p className="text-white/70 text-sm">Confirmation sent to <strong className="text-white">{form.email}</strong></p>
          </motion.div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">
        {/* Order Number */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-sm p-5 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Order Number</p>
            <p className="font-black text-2xl text-[#1a3a6e] mt-0.5">{orderId}</p>
          </div>
          <div className="w-12 h-12 bg-[#1a3a6e]/10 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-[#1a3a6e]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
          </div>
        </motion.div>

        {/* Parcel Notice */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
            <div>
              <p className="font-black text-amber-800 text-sm mb-1">IMPORTANT — Please Read</p>
              <p className="text-amber-700 text-sm font-semibold">✓ First CHECK the parcel, THEN pay the rider.</p>
              <p className="text-amber-600 text-xs mt-1">No breakage or any claim will be accepted after payment is made to the rider.</p>
            </div>
          </div>
        </motion.div>

        {/* Items */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100"><h3 className="font-bold text-gray-900">Items Ordered</h3></div>
          <div className="divide-y divide-gray-50">
            {items.map((item, i) => {
              const itemPrice = item.salePrice && item.salePrice < item.price ? item.salePrice : item.price;
              return (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="relative flex-shrink-0">
                    <img src={item.images?.[0] || 'https://via.placeholder.com/48'} alt={item.title}
                      className="w-14 h-14 object-cover rounded-lg" onError={e => e.target.src = 'https://via.placeholder.com/48'}/>
                    <span className="absolute -top-1.5 -right-1.5 bg-[#1a3a6e] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-black">{item.quantity}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">{item.title}</p>
                    {item.selectedVariation && <p className="text-gray-400 text-xs">{item.selectedVariation}</p>}
                    <p className="text-gray-400 text-xs">Qty: {item.quantity} × {formatPrice(itemPrice)}</p>
                  </div>
                  <p className="font-bold text-sm text-gray-900 flex-shrink-0">{formatPrice(itemPrice * item.quantity)}</p>
                </div>
              );
            })}
          </div>
          <div className="px-5 py-4 bg-gray-50 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Shipping ({shippingRule?.label || 'Standard'})</span><span>{formatPrice(shippingCost)}</span></div>
            <div className="flex justify-between font-black text-base border-t border-gray-200 pt-2">
              <span>Total</span><span className="text-[#1a3a6e]">{formatPrice(total)}</span>
            </div>
          </div>
        </motion.div>

        {/* Customer + Shipping */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Contact</h3>
            <p className="text-gray-700 text-sm font-medium">{form.firstName} {form.lastName}</p>
            <p className="text-gray-500 text-sm">{form.email}</p>
            <p className="text-gray-500 text-sm">{form.phone}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Shipping To</h3>
            <p className="text-gray-700 text-sm">{form.address}</p>
            <p className="text-gray-500 text-sm">{form.city}, Pakistan</p>
            <p className="text-[#1a3a6e] text-xs font-semibold mt-1.5 capitalize">
              Payment: {form.paymentMethod === 'cod' ? 'Cash on Delivery' : form.paymentMethod}
            </p>
          </div>
        </motion.div>

        {/* What's Next */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-4">What Happens Next?</h3>
          <div className="space-y-3">
            {[
              'Our team will confirm your order via WhatsApp/call within a few hours.',
              'Your order will be packed and dispatched within 1-2 business days.',
              'You\'ll receive tracking info once your order ships.',
              'CHECK the parcel first, THEN pay the rider. No claims after payment.',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#1a3a6e] text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 mt-0.5">{i + 1}</div>
                <p className="text-sm text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="flex gap-3 pb-6">
          <Link to="/" className="flex-1 btn-outline text-sm py-3 text-center">Continue Shopping</Link>
          {user && <Link to="/account/orders" className="flex-1 btn-primary text-sm py-3 text-center">View My Orders</Link>}
        </motion.div>
      </div>
    </div>
  );

  const STEPS = ['Contact & Shipping', 'Payment', 'Review'];

  return (
    <>
      <SEO title="Checkout"/>
      <div className="min-h-screen bg-[#f8f9fc]">
        <div className="bg-[#0f2347] py-6 px-4">
          <div className="max-w-5xl mx-auto">
            <Link to="/cart" className="text-white/60 text-sm hover:text-white flex items-center gap-1.5 mb-4">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
              Back to Cart
            </Link>
            <div className="flex items-center gap-0">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center">
                  <div className={`flex items-center gap-2 text-xs font-bold ${i <= step ? 'text-white' : 'text-white/30'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 ${i < step ? 'bg-green-500' : i === step ? 'bg-[#e63946]' : 'bg-white/20'}`}>
                      {i < step ? <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg> : i + 1}
                    </div>
                    <span className="hidden sm:block">{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`w-8 sm:w-16 h-px mx-2 ${i < step ? 'bg-green-400' : 'bg-white/20'}`}/>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white shadow-sm p-6">
                <AnimatePresence mode="wait">
                  {/* Step 0 */}
                  {step === 0 && (
                    <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="font-bold text-lg text-gray-900 mb-6">Contact & Shipping</h2>
                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <div><label className="block text-xs font-bold text-gray-700 mb-1.5">First Name *</label>
                          <input className="input-field" value={form.firstName} onChange={e => update('firstName', e.target.value)} placeholder="Muhammad"/></div>
                        <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Last Name</label>
                          <input className="input-field" value={form.lastName} onChange={e => update('lastName', e.target.value)} placeholder="Ahmed"/></div>
                        <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Email *</label>
                          <input type="email" className="input-field" value={form.email} onChange={e => update('email', e.target.value)} placeholder="email@example.com"/></div>
                        <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Phone *</label>
                          <input className="input-field" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="0300-0000000"/></div>
                      </div>
                      <div className="mb-4"><label className="block text-xs font-bold text-gray-700 mb-1.5">Street Address *</label>
                        <input className="input-field" value={form.address} onChange={e => update('address', e.target.value)} placeholder="House No., Street, Area"/></div>
                      <div className="mb-4"><label className="block text-xs font-bold text-gray-700 mb-1.5">City *</label>
                        <select className="input-field" value={form.city} onChange={e => { update('city', e.target.value); update('paymentMethod', ''); }}>
                          <option value="">Select City</option>
                          {PAKISTAN_CITIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      {/* Shipping info box */}
                      {form.city && (
                        <div className={`p-4 rounded-lg mb-4 border ${shippingRule ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-sm font-bold ${shippingRule ? 'text-blue-800' : 'text-orange-800'}`}>
                                {shippingRule ? `Shipping: ${shippingRule.label}` : `Shipping to ${form.city}`}
                              </p>
                              <p className={`text-xs mt-0.5 ${shippingRule ? 'text-blue-600' : 'text-orange-600'}`}>
                                {totalQty} item(s)
                                {!shippingRule && ' · Standard rate applied'}
                                {shippingRule && !shippingRule.codAllowed && <span className="ml-1 font-semibold text-orange-600">· Advance Payment Required</span>}
                              </p>
                            </div>
                            <span className={`font-black text-lg ${shippingRule ? 'text-blue-800' : 'text-orange-800'}`}>{formatPrice(shippingCost)}</span>
                          </div>
                        </div>
                      )}
                      <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Order Notes (optional)</label>
                        <textarea rows={3} className="input-field resize-none" value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Any special instructions..."/></div>
                    </motion.div>
                  )}

                  {/* Step 1 - Payment */}
                  {step === 1 && (
                    <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="font-bold text-lg text-gray-900 mb-2">Payment Method</h2>
                      {!codAllowed && form.city && (
                        <div className="bg-orange-50 border border-orange-200 p-3 mb-4 rounded-lg text-xs text-orange-800 font-medium">
                          {totalQty > 2
                            ? 'Orders of 3+ items require advance payment — COD not available.'
                            : 'Cash on Delivery is only available in Karachi. Please choose advance payment.'}
                        </div>
                      )}
                      <div className="space-y-2">
                        {getPaymentMethods().map(pm => (
                          <div key={pm.id}>
                            <div
                              onClick={() => {
                                update('paymentMethod', pm.id);
                                if (pm.id !== 'cod') {
                                  setOpenPayInfo(prev => prev === pm.id ? null : pm.id);
                                } else {
                                  setOpenPayInfo(null);
                                }
                              }}
                              className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-all rounded-xl select-none ${form.paymentMethod === pm.id ? 'border-[#1a3a6e] bg-[#1a3a6e]/5' : 'border-gray-200 hover:border-gray-300'}`}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${form.paymentMethod === pm.id ? 'border-[#1a3a6e] bg-[#1a3a6e]' : 'border-gray-300'}`}>
                                {form.paymentMethod === pm.id && <div className="w-2 h-2 bg-white rounded-full"/>}
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-sm text-gray-900">{pm.label}</p>
                                <p className="text-xs text-gray-500">{pm.sub}</p>
                              </div>
                              {pm.id !== 'cod' && (
                                <motion.div animate={{ rotate: openPayInfo === pm.id ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                                </motion.div>
                              )}
                            </div>
                            {/* Collapse Panel */}
                            <AnimatePresence initial={false}>
                              {openPayInfo === pm.id && pm.id !== 'cod' && (
                                <motion.div
                                  key={pm.id}
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                                  style={{ overflow: 'hidden' }}
                                >
                                  <div className="border-2 border-t-0 border-[#1a3a6e] bg-[#1a3a6e]/5 p-4 space-y-2 rounded-b-xl">
                                    {pm.id === 'jazzcash' && paymentAccounts.jazzcash?.number && (
                                      <>
                                        <p className="text-xs font-black text-gray-700 uppercase tracking-wider mb-2">JazzCash Account Details</p>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Number:</span><strong className="text-gray-900 select-all">{paymentAccounts.jazzcash.number}</strong></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Name:</span><strong className="text-gray-900">{paymentAccounts.jazzcash.name}</strong></div>
                                        <p className="text-xs text-[#1a3a6e] mt-2 font-semibold">Send payment then place your order. Share screenshot on WhatsApp.</p>
                                      </>
                                    )}
                                    {pm.id === 'easypaisa' && paymentAccounts.easypaisa?.number && (
                                      <>
                                        <p className="text-xs font-black text-gray-700 uppercase tracking-wider mb-2">EasyPaisa Account Details</p>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Number:</span><strong className="text-gray-900 select-all">{paymentAccounts.easypaisa.number}</strong></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Name:</span><strong className="text-gray-900">{paymentAccounts.easypaisa.name}</strong></div>
                                        <p className="text-xs text-[#1a3a6e] mt-2 font-semibold">Send payment then place your order. Share screenshot on WhatsApp.</p>
                                      </>
                                    )}
                                    {pm.id === 'bank' && paymentAccounts.bank?.accountNo && (
                                      <>
                                        <p className="text-xs font-black text-gray-700 uppercase tracking-wider mb-2">Bank Transfer Details</p>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Bank:</span><strong className="text-gray-900">{paymentAccounts.bank.bankName}</strong></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Account No:</span><strong className="text-gray-900 select-all">{paymentAccounts.bank.accountNo}</strong></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Title:</span><strong className="text-gray-900">{paymentAccounts.bank.accountTitle}</strong></div>
                                        {paymentAccounts.bank.iban && <div className="flex justify-between text-sm"><span className="text-gray-500">IBAN:</span><strong className="text-gray-900 select-all text-xs">{paymentAccounts.bank.iban}</strong></div>}
                                        <p className="text-xs text-[#1a3a6e] mt-2 font-semibold">Transfer amount then place order. Share screenshot on WhatsApp.</p>
                                      </>
                                    )}
                                    {(pm.id === 'jazzcash' && !paymentAccounts.jazzcash?.number) ||
                                     (pm.id === 'easypaisa' && !paymentAccounts.easypaisa?.number) ||
                                     (pm.id === 'bank' && !paymentAccounts.bank?.accountNo) ? (
                                      <p className="text-xs text-gray-500">Account details will be provided via WhatsApp after order placement.</p>
                                    ) : null}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2 - Review */}
                  {step === 2 && (
                    <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="font-bold text-lg text-gray-900 mb-5">Review & Confirm</h2>
                      <div className="bg-amber-50 border-2 border-amber-300 p-4 rounded-xl mb-5">
                        <p className="text-xs font-black text-amber-800 uppercase tracking-wider mb-1">Important</p>
                        <p className="text-sm font-semibold text-amber-700">✓ First CHECK the parcel, THEN pay the rider.</p>
                        <p className="text-xs text-amber-600 mt-0.5">No breakage or any claim accepted after payment.</p>
                      </div>
                      <div className="space-y-4 text-sm">
                        <div className="bg-gray-50 p-4 rounded-xl">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Delivering To</p>
                          <p className="font-semibold text-gray-900">{form.firstName} {form.lastName} — {form.phone}</p>
                          <p className="text-gray-500">{form.address}, {form.city}, Pakistan</p>
                          <p className="text-gray-400 text-xs mt-1">Shipping: {shippingRule?.label || 'Standard'} · {formatPrice(shippingCost)}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Payment</p>
                          <p className="font-semibold text-gray-900 capitalize">{form.paymentMethod === 'cod' ? 'Cash on Delivery' : form.paymentMethod}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl space-y-3">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Items ({items.length})</p>
                          {items.map(item => {
                            const ip = item.salePrice && item.salePrice < item.price ? item.salePrice : item.price;
                            return (
                              <div key={`${item.id}_${item.selectedVariation}`} className="flex items-center gap-3">
                                <img src={item.images?.[0] || 'https://via.placeholder.com/40'} alt={item.title} className="w-10 h-10 object-cover rounded-lg" onError={e => e.target.src = 'https://via.placeholder.com/40'}/>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-xs truncate">{item.title}</p>
                                  {item.selectedVariation && <p className="text-gray-400 text-xs">{item.selectedVariation}</p>}
                                  <p className="text-gray-400 text-xs">Qty: {item.quantity}</p>
                                </div>
                                <p className="font-bold text-xs">{formatPrice(ip * item.quantity)}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                  {step > 0 ? <button onClick={() => setStep(s => s - 1)} className="btn-outline text-sm py-2.5 px-6">Back</button> : <div/>}
                  {step < 2 ? (
                    <button onClick={() => { if (validateStep()) setStep(s => s + 1); }} className="btn-primary text-sm py-2.5 px-8 flex items-center gap-2">
                      Continue
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                    </button>
                  ) : (
                    <button onClick={handlePlace} disabled={loading} className="btn-accent text-sm py-2.5 px-8 flex items-center gap-2">
                      {loading ? <Spinner size="sm" color="white"/> : <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>}
                      {loading ? 'Placing Order...' : 'Place Order'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Sidebar */}
            <div>
              <div className="bg-white shadow-sm p-5 sticky top-4 rounded-2xl">
                <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Order Summary</h3>
                <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                  {items.map(item => {
                    const ip = item.salePrice && item.salePrice < item.price ? item.salePrice : item.price;
                    return (
                      <div key={`${item.id}_${item.selectedVariation}`} className="flex gap-3">
                        <div className="relative flex-shrink-0">
                          <img src={item.images?.[0] || 'https://via.placeholder.com/44'} alt={item.title} className="w-11 h-11 object-cover rounded-lg" onError={e => e.target.src = 'https://via.placeholder.com/44'}/>
                          <span className="absolute -top-1.5 -right-1.5 bg-[#1a3a6e] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-black">{item.quantity}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium leading-tight truncate">{item.title}</p>
                          {item.selectedVariation && <p className="text-gray-400 text-xs">{item.selectedVariation}</p>}
                        </div>
                        <p className="text-xs font-bold flex-shrink-0">{formatPrice(ip * item.quantity)}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                  <div className="flex justify-between text-gray-500">
                    <span>Shipping</span>
                    <span>{!form.city ? '—' : formatPrice(shippingCost)}</span>
                  </div>
                  <div className="flex justify-between font-black text-base border-t border-gray-100 pt-2">
                    <span>Total</span><span className="text-[#1a3a6e]">{formatPrice(total)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-1.5 mt-4 text-gray-400 text-xs">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                  Secure Checkout
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default CheckoutPage;
